import React from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { PALETTE } from './constants';
import { DashboardLoading, daysSince, ownerCount, ownerName, sumCounts } from './dashboard/dashboardFormatting';
import FocusTable, { Chip } from './dashboard/FocusTable';
import HBars from './dashboard/HBars';
import RadioDashboardSkeleton from './dashboard/RadioDashboardSkeleton';
import RadioDonut from './dashboard/RadioDonut';
import { useGeneralRisk } from './hooks/useGeneralRisk';

// IB-677 rework — Issues dashboard on the shared skeleton. Profile = severity split;
// key metric = severity profile; focus table = high-priority issues.

export default function GeneralRiskDashboardIssues() {
    const { dashboardData, loading } = useGeneralRisk();

    if (loading.dashboard) {
        return <DashboardLoading />;
    }

    const issues = dashboardData?.issues || {};
    const overview = dashboardData?.overview || {};
    const priorities = issues.open_by_priority_counts || {};
    const open = sumCounts(priorities);

    const tiles = [
        { lead: true, label: $t('Open issues'), value: open, sub: `${priorities.high ?? 0} ${$t('high priority')}` },
        { label: $t('High priority'), value: priorities.high ?? 0 },
        { tone: 'red', label: $t('No update > 14 days'), value: issues.stale_over_14_count ?? 0 },
        {
            tone: 'warn',
            label: $t('Overdue actions'),
            value: issues.overdue_actions_count ?? 0,
            sub: `${$t('across')} ${ownerCount(issues.overdue_actions_count_by_owner)} ${$t('owners')}`,
        },
        { label: $t('Open actions'), value: issues.open_actions_count ?? 0 },
    ];

    const profile = {
        title: $t('Severity profile'),
        content: (
            <RadioDonut
                total={`${open} ${$t('open')}`}
                segments={[
                    { value: priorities.high ?? 0, colour: PALETTE.red, label: `${$t('Severe')} · ${priorities.high ?? 0}` },
                    { value: priorities.medium ?? 0, colour: PALETTE.red50, label: `${$t('Moderate')} · ${priorities.medium ?? 0}` },
                    { value: priorities.low ?? 0, colour: PALETTE.charcoal25, label: `${$t('Minor')} · ${priorities.low ?? 0}` },
                ]}
            />
        ),
    };

    const keyMetric = {
        title: $t('Severity profile'),
        content: (
            <HBars
                labelWidth={72}
                rows={[
                    { label: $t('Severe'), segments: [{ value: priorities.high ?? 0, colourClass: 'tw-bg-red' }] },
                    { label: $t('Moderate'), segments: [{ value: priorities.medium ?? 0, colourClass: 'tw-bg-red-50' }] },
                    { label: $t('Minor'), segments: [{ value: priorities.low ?? 0, colourClass: 'tw-bg-charcoal-25' }] },
                ]}
                emptyLabel={$t('No open issues')}
            />
        ),
    };

    const highPriorities = issues.high_priorities || [];
    const focus = (
        <FocusTable
            title={$t('High priority issues')}
            columns={[$t('Issue'), $t('Severity'), $t('Last update'), $t('Owner')]}
            rows={highPriorities.map((issue) => [
                issue.description || issue.id,
                <Chip key="s">{issue.priority || '—'}</Chip>,
                `${daysSince(issue.updated_at) ?? 0}${$t('d ago')}`,
                ownerName(issue.owner),
            ])}
            empty={$t('No high priority issues')}
        />
    );

    return (
        <RadioDashboardSkeleton
            tiles={tiles}
            profile={profile}
            movement={{ data: overview.movement, category: 'issues' }}
            keyMetric={keyMetric}
            focus={focus}
        />
    );
}

if (document.getElementById('generalRiskDashboardIssues')) {
    const root = document.getElementById('generalRiskDashboardIssues');
    ReactDOM.render(
        <App>
            <GeneralRiskDashboardIssues />
        </App>,
        root,
    );
}
